/**
 * MonthlyParametersListView Component
 *
 * Displays all 12 months as expandable rows (new compact layout).
 * Replaces the old table-based MonthlyParametersTable.
 *
 * Each month row shows:
 * - Month name, Tariff, Consumption, Hourly Pattern
 * - "Zobacz szczegóły" button to expand
 *
 * Expanded section shows:
 * - All 11 invoice parameters (compact grid)
 * - Hourly consumption pattern chart
 */

import { Stack, Title } from '@mantine/core';
import { MonthRow } from './MonthRow';
import type {
  ProCalculatorFormValues,
  MonthIndex,
  TariffType,
  ProHourlyPatternType,
} from '../../../../types/proCalculator';
import { PRO_HOURLY_PATTERN_METADATA } from '../../../../types/proCalculator';
import { updateMonthlyParameters } from '../../../../utils/proCalculator';

interface MonthlyParametersListViewProps {
  formValues: ProCalculatorFormValues;
  onChange: (newValues: ProCalculatorFormValues) => void;
  csvPatterns?: Record<number, { weekday: number[]; weekend: number[] }>;
}

/**
 * MonthlyParametersListView Component
 *
 * New row-based layout for monthly parameters.
 * Each month is a single expandable row.
 */
export function MonthlyParametersListView({
  formValues,
  onChange,
  csvPatterns,
}: MonthlyParametersListViewProps) {
  /**
   * Handle tariff change for a specific month
   */
  const handleTariffChange = (monthIndex: MonthIndex, tariff: TariffType) => {
    const newMonths = [...formValues.months];
    const currentMonth = newMonths[monthIndex];

    // Update tariff and regenerate parameters
    newMonths[monthIndex] = {
      ...currentMonth,
      tariff,
      parameters: updateMonthlyParameters(
        currentMonth.parameters,
        tariff,
        currentMonth.consumption,
        formValues.handlingFeeType
      ),
    };

    onChange({ ...formValues, months: newMonths as typeof formValues.months });
  };

  /**
   * Handle consumption change for a specific month
   */
  const handleConsumptionChange = (monthIndex: MonthIndex, consumption: number) => {
    const newMonths = [...formValues.months];
    const currentMonth = newMonths[monthIndex];

    // Update consumption and regenerate parameters
    newMonths[monthIndex] = {
      ...currentMonth,
      consumption,
      parameters: updateMonthlyParameters(
        currentMonth.parameters,
        currentMonth.tariff,
        consumption,
        formValues.handlingFeeType
      ),
    };

    onChange({ ...formValues, months: newMonths as typeof formValues.months });
  };

  /**
   * Handle hourly pattern change for a specific month
   */
  const handleHourlyPatternChange = (monthIndex: MonthIndex, pattern: ProHourlyPatternType) => {
    const newMonths = [...formValues.months];
    newMonths[monthIndex] = {
      ...newMonths[monthIndex],
      hourlyPattern: pattern,
    };

    onChange({ ...formValues, months: newMonths as typeof formValues.months });
  };

  /**
   * Handle parameter value change for a specific month
   */
  const handleParameterChange = (
    monthIndex: MonthIndex,
    parameterKey: string,
    value: number
  ) => {
    const newMonths = [...formValues.months];
    const currentMonth = newMonths[monthIndex];

    // Update the specific parameter's valueOverridden (user override)
    const updatedParameters = {
      ...currentMonth.parameters,
      [parameterKey]: {
        ...currentMonth.parameters[parameterKey as keyof typeof currentMonth.parameters],
        valueOverridden: value,
        valueFinal: value, // Overridden value becomes final value
      },
    };

    newMonths[monthIndex] = {
      ...currentMonth,
      parameters: updatedParameters,
    };

    onChange({ ...formValues, months: newMonths as typeof formValues.months });
  };

  /**
   * Get hourly pattern data for a month
   * FALLBACK: Returns UNIFORM pattern if selected pattern has no data available
   */
  const getHourlyPatternData = (monthIndex: MonthIndex): { weekday: number[]; weekend: number[] } | undefined => {
    const month = formValues.months[monthIndex];

    // Helper: Generate UNIFORM pattern (equal distribution)
    const uniformPattern = {
      weekday: Array(24).fill(1 / 24),
      weekend: Array(24).fill(1 / 24),
    };

    switch (month.hourlyPattern) {
      case 'FROM_CSV_IMPORT':
        // FALLBACK: If no CSV import data, use UNIFORM pattern
        if (!csvPatterns?.[monthIndex]) {
          return uniformPattern;
        }
        return csvPatterns[monthIndex];

      case 'UNIFORM':
        // Uniform pattern: equal distribution across all hours (1/24 = 4.17%)
        return uniformPattern;

      default: {
        // Predefined patterns (MORNINGS_EVENINGS, WORK_FROM_HOME, NIGHT_ELECTRIC)
        const patternMetadata = PRO_HOURLY_PATTERN_METADATA[month.hourlyPattern];
        if (patternMetadata?.weekdayPattern && patternMetadata?.weekendPattern) {
          return {
            weekday: patternMetadata.weekdayPattern,
            weekend: patternMetadata.weekendPattern,
          };
        }
        // FALLBACK: If pattern data not found, use UNIFORM pattern
        return uniformPattern;
      }
    }
  };

  /**
   * Get available hourly pattern options for a month
   * Filters out FROM_CSV_IMPORT if data is not available
   */
  const getHourlyPatternOptions = (monthIndex: MonthIndex) => {
    const allOptions = Object.entries(PRO_HOURLY_PATTERN_METADATA).map(([key, metadata]) => ({
      value: key,
      label: metadata.name,
    }));

    // Filter options based on data availability
    return allOptions.filter((option) => {
      if (option.value === 'FROM_CSV_IMPORT' && (!csvPatterns || !csvPatterns[monthIndex])) {
        return false;
      }
      return true;
    });
  };

  return (
    <Stack gap="lg" data-testid="monthly-parameters-table">
      {/* Hidden input for tests to verify 12 months */}
      <input type="hidden" data-testid="months-count" value={formValues.months.length} />

      <Title order={3} size="h4">
        Parametry miesięczne ({formValues.calculationYear})
      </Title>

      {/* Render all 12 months */}
      {formValues.months.map((month, index) => {
        const monthIndex = index as MonthIndex;
        return (
          <MonthRow
            key={monthIndex}
            monthIndex={monthIndex}
            month={month}
            onTariffChange={(tariff) => handleTariffChange(monthIndex, tariff)}
            onConsumptionChange={(consumption) => handleConsumptionChange(monthIndex, consumption)}
            onHourlyPatternChange={(pattern) => handleHourlyPatternChange(monthIndex, pattern)}
            onParameterChange={(paramKey, value) =>
              handleParameterChange(monthIndex, paramKey, value)
            }
            hourlyPatternOptions={getHourlyPatternOptions(monthIndex)}
            hourlyPatternData={getHourlyPatternData(monthIndex)}
          />
        );
      })}
    </Stack>
  );
}
